// =============================================================================
// Finance batch review screen — one batch's review queue + batch-wide actions.
// Exception cockpit: "Needs you" (not yet sent) vs "Auto-sent ⚡".
// Toolbar toasts echo the server's counters (verified/matched/short/over,
// approved-clean/skipped, reconciled drift, backfilled) per the spec.
// =============================================================================
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Alert, Badge, Button, Spinner, Table } from "react-bootstrap";
import { FaChevronLeft, FaSync } from "react-icons/fa";
import { apiFetch, hasAction } from "../utils/auth";
import { MATCH_BADGE, decisionLabel, money } from "./financeShared";
import "../pagecss/Webappcss.css";
import "../pagecss/QuoteWizard.css";

const API_URL = process.env.REACT_APP_API_URL;

export default function BatchDetail() {
  const { batchCode } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [batch, setBatch] = useState(location.state?.batch || null);
  const [queue, setQueue] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [note, setNote] = useState(null);
  const [busy, setBusy] = useState(null);
  const [subTab, setSubTab] = useState("needs");   // needs | sent

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const calls = [apiFetch(`${API_URL}/api/invoices/review-queue?batchCode=${encodeURIComponent(batchCode)}&pageSize=500`)];
      if (!batch) calls.push(apiFetch(`${API_URL}/api/invoices/batches`));
      const [qRes, bRes] = await Promise.all(calls);
      const q = await qRes.json().catch(() => null);
      if (!qRes.ok || q?.success === false) throw new Error(q?.error || `Load failed (HTTP ${qRes.status}).`);
      setQueue(q);
      if (bRes) {
        const list = await bRes.json().catch(() => []);
        setBatch((Array.isArray(list) ? list : []).find((b) => b.batchCode === batchCode) || null);
      }
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [batchCode, batch]);

  useEffect(() => { load(); }, []);   // eslint-disable-line react-hooks/exhaustive-deps

  const items = useMemo(() => queue?.items || [], [queue]);
  const needs = useMemo(() => items.filter((r) => r.sendStatus !== "Sent"), [items]);
  const sent = useMemo(() => items.filter((r) => r.sendStatus === "Sent"), [items]);
  const rows = subTab === "needs" ? needs : sent;

  // ── batch-wide actions ─────────────────────────────────────────────────────
  const act = async (key, path, confirmMsg, toast) => {
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    setBusy(key); setError(null); setNote(null);
    try {
      const r = await apiFetch(`${API_URL}${path}`, { method: "POST" });
      const d = await r.json().catch(() => null);
      if (!r.ok || d?.success === false) throw new Error(d?.error || d?.message || `${key} failed (HTTP ${r.status}).`);
      setNote(toast(d));
      await load();
    } catch (e) { setError(e.message); }
    finally { setBusy(null); }
  };

  const bid = batch?.id;
  const toolbar = [
    hasAction("00075", true) && bid && {
      key: "verify", label: "↻ Verify batch (3-way match)",
      title: "Warms the SalesOrderCache in live — up to ~5 min on the first call after a server restart",
      run: () => act("verify", `/api/invoices/batches/${bid}/verify`, null,
        (d) => `Verified ${d.rowsUpdated ?? d.checked ?? "?"} rows · M${d.matched ?? 0}/S${d.short ?? 0}/O${d.over ?? 0}`),
    },
    hasAction("00075", true) && bid && batch?.planCategory !== "CASH" && {
      key: "verify-inst", label: "↻ Verify installments",
      run: () => act("verify-inst", `/api/invoices/batches/${bid}/verify-installments`, null,
        (d) => `Installments checked ${d.installmentsChecked ?? "?"} · matched ${d.matched ?? 0} · short ${d.short ?? 0} · over ${d.over ?? 0}${d.capBreach ? ` · ⚠ capBreach ${d.capBreach}` : ""}`),
    },
    hasAction("00076", true) && bid && {
      key: "approve-clean", label: "✓ Approve clean",
      run: () => act("approve-clean", `/api/invoices/batches/${bid}/approve-clean`,
        "Bulk-approve every Matched/NoCharge invoice in this batch?",
        (d) => `Approved ${d.approved ?? 0} clean · ${d.skippedFlagged ?? 0} flagged left`),
    },
    hasAction("00077", true) && bid && {
      key: "send-approved", label: "✉ Send approved",
      run: () => act("send-approved", `/api/invoices/batches/${bid}/send-approved`,
        "Send every approved-unsent invoice in this batch to its customer AP contact (CC the rep)?\n(SIMULATED in dev until InvoiceSending:Enabled.)",
        (d) => d?.message || `Sent ${d.sent ?? 0} invoice(s).`),
    },
    hasAction("00075", true) && bid && {
      key: "reconcile", label: "⟲ Reconcile",
      run: () => act("reconcile", `/api/invoices/batches/${bid}/reconcile`, null,
        (d) => `Reconciled · drift ${d.driftBounced ?? 0} · sent-edited ${d.sentThenEdited ?? 0} · backfilled ${d.backfilled ?? 0}`),
    },
    hasAction("00075", true) && bid && {
      key: "backfill", label: "＋ Backfill orphans",
      run: () => act("backfill", `/api/invoices/batches/${bid}/backfill`, null,
        (d) => `Backfilled ${d.backfilled ?? 0} of ${d.epicorInvoicesInGroup ?? "?"} in group`),
    },
    hasAction("00078", true) && bid && batch?.status !== "Posted" && {
      key: "mark-posted", label: "Mark posted ✓",
      run: () => act("mark-posted", `/api/invoices/batches/${bid}/mark-posted`,
        `Record that finance posted batch ${batchCode} in Epicor?`,
        (d) => d?.message || "Marked posted."),
    },
  ].filter(Boolean);

  const chip = (active) => ({
    padding: "5px 12px", borderRadius: 999, fontSize: 12.5, fontWeight: 600, cursor: "pointer",
    border: active ? "1.5px solid #4F46E5" : "1px solid #E2E8F0",
    background: active ? "#EEF2FF" : "#fff", color: active ? "#4338CA" : "#475569",
  });

  return (
    <div className="qw-root content-halo" style={{ padding: "28px 24px 36px", maxWidth: "min(1180px, 96vw)", margin: "84px auto 56px", borderRadius: 16, minHeight: "calc(100vh - 140px)" }}>
      <div className="qw-header" style={{ marginBottom: 16 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <div>
            <div className="qw-header-title">{batchCode}</div>
            <div className="qw-header-sub">
              {queue ? `${queue.total} invoices · ${money(queue.totalAmount)}` : "…"}
              {batch?.epicorGroupId ? ` · group ${batch.epicorGroupId}` : ""}
            </div>
          </div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <Button className="qw-btn qw-btn-sm" style={{ background: "rgba(255,255,255,.18)", color: "#fff", border: "none" }} onClick={load}><FaSync /></Button>
          <Button className="qw-btn qw-btn-sm" style={{ background: "rgba(255,255,255,.18)", color: "#fff" }} onClick={() => navigate("/finance")}>
            <FaChevronLeft style={{ marginRight: 6 }} /> Back
          </Button>
        </div>
      </div>

      {/* Match / decision chips */}
      {queue && (
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
          {Object.entries(queue.matchCounts || {}).map(([k, v]) => (
            <Badge key={k} bg={MATCH_BADGE[k] || "secondary"} style={{ fontSize: 12 }}>{k}: {v}</Badge>
          ))}
          {Object.entries(queue.decisionCounts || {}).map(([k, v]) => (
            <Badge key={k} bg="light" text="dark" style={{ fontSize: 12, border: "1px solid #E2E8F0" }}>{k}: {v}</Badge>
          ))}
        </div>
      )}

      {/* Toolbar — the action buttons share the full row width equally. */}
      <div style={{ display: "flex", gap: 8, flexWrap: "nowrap", marginBottom: 12 }}>
        {toolbar.map((t) => (
          <Button key={t.key} size="sm" variant="outline-secondary" disabled={!!busy} title={t.title} onClick={t.run}
            style={{ flex: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {busy === t.key ? <Spinner animation="border" size="sm" /> : t.label}
          </Button>
        ))}
      </div>

      {note && <Alert variant="success" dismissible onClose={() => setNote(null)} style={{ fontSize: 13 }}>{note}</Alert>}
      {error && <Alert variant="danger" dismissible onClose={() => setError(null)} style={{ fontSize: 13 }}>{error}</Alert>}

      <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
        <span style={chip(subTab === "needs")} onClick={() => setSubTab("needs")}>
          Needs you {needs.length > 0 && <Badge bg="danger" pill>{needs.length}</Badge>}
        </span>
        <span style={chip(subTab === "sent")} onClick={() => setSubTab("sent")}>Auto-sent ⚡ ({sent.length})</span>
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: 48, color: "#64748B" }}><Spinner animation="border" size="sm" /> Loading review queue…</div>
      ) : (
        <div style={{ border: "1px solid #E2E8F0", borderRadius: 12, overflow: "hidden", background: "#fff" }}>
          {rows.length === 0 ? (
            <div style={{ textAlign: "center", padding: 28, color: "#64748B" }}>
              {subTab === "needs" ? "✓ Nothing needs your review here — the system handled it all." : "No invoices auto-sent in this batch yet."}
            </div>
          ) : (
            <Table hover size="sm" style={{ fontSize: 13, marginBottom: 0 }}>
              <thead>
                <tr><th>Invoice #</th><th>Customer</th><th>PO #</th><th>Order</th><th style={{ textAlign: "right" }}>Order</th><th style={{ textAlign: "right" }}>Invoice</th><th>Match</th><th>Stage</th><th /></tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const dec = decisionLabel(r);
                  const flagged = !["Matched", "NoCharge"].includes(r.matchStatus);
                  return (
                    <tr key={r.id} style={r.isHeld ? { background: "#FEF2F2" } : flagged ? { background: "#FFFBEB" } : undefined}>
                      <td style={{ fontWeight: 700 }}>{r.invoiceNum}{r.isHeld && <span title={r.holdReason || undefined}> 🔒</span>}</td>
                      <td>{r.customerName || r.custNum}</td>
                      <td style={{ fontFamily: "monospace", fontSize: 12 }}>{r.customerPONum || "—"}</td>
                      <td>{r.orderNum}</td>
                      <td style={{ textAlign: "right" }}>{money(r.orderAmount)}</td>
                      <td style={{ textAlign: "right", fontWeight: 600 }}>{money(r.invoiceAmount)}</td>
                      <td><Badge bg={MATCH_BADGE[r.matchStatus] || "secondary"}>{r.matchStatus}</Badge></td>
                      <td><Badge bg={dec.bg} text={dec.bg === "warning" ? "dark" : undefined}>{dec.text}</Badge></td>
                      <td>
                        <Button size="sm" variant={flagged ? "warning" : "outline-primary"}
                          onClick={() => navigate(`/finance/invoice/${r.invoiceNum}`, { state: { review: r, batch } })}>
                          {flagged ? "Review →" : "View"}
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          )}
        </div>
      )}
      <div style={{ fontSize: 11.5, color: "#94A3B8", marginTop: 8 }}>
        {rows.length} of {items.length} in this batch · {subTab === "needs" ? "needs review" : "auto-sent"} · GET /api/invoices/review-queue?batchCode={batchCode}
      </div>
    </div>
  );
}
